import { IMsalContext } from "@azure/msal-react";
import axios from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL } from "const";

// ---------------------------------------------------------------------------
// Combined governance group + entitlement creation
// ---------------------------------------------------------------------------

export interface ICombinedGroupPayload {
    governance_name: string;
    governance_description: string;
    governance_owner: string;
    /** Backend requires at least two members for the approval workflow. */
    governance_member: string[];
    entitlement_name: string;
    entitlement_description: string;
    /** IAM supports a single entitlement owner, so this is one email, not a list. */
    entitlement_owner: string;
    entitlementAdditionalOwners: string[];
    entitlementAllowAccessRequests: boolean;
    entitlementGroupType: string;
}

/**
 * Trigger creation of a governance group and its entitlement in a single call.
 * The backend creates the governance group and wires it as the entitlement's
 * approver, so no separate approver-group selection is required.
 */
export const createCombinedGroup = async (
    msalInstance: IMsalContext,
    payload: ICombinedGroupPayload,
): Promise<void> => {
    const token = await getAccessToken(msalInstance);
    const bearerToken = "Bearer " + (token?.token ?? "");

    const requestURL = `${NEXUS_API_BASE_URL}/iam/groups/combined`;

    await axios.post(requestURL, payload, {
        headers: {
            Authorization: bearerToken,
            "Content-Type": "application/json",
        },
    });
};

// ---------------------------------------------------------------------------
// IAM group listing + deletion
// ---------------------------------------------------------------------------

/**
 * A single row in the combined groups listing. Each row represents a group
 * pair — a governance group and its entitlement — returned together by the
 * same endpoint. Deletion removes the entitlement first, then the governance
 * group.
 */
export interface IGroupListItem {
    group_id: string;
    governance_name?: string;
    entitlement_name?: string;
    /** Single email of the governance group owner; determines delete visibility. */
    governance_owner?: string;
    governance_member?: string[];
    /** Older records may still hold a list; newer ones store a single email. */
    entitlement_owner?: string | string[];
    group_type?: string;
    /** GUID of the governance group; sent as `id` when deleting it. */
    governance_group_id?: string;
    /** Distinguished name of the entitlement; sent as `value` when deleting it. */
    entitlement_group_id?: string;
    created_at?: string;
    created_by?: string;
    idc_sync_status?: string;
    group_creation_status?: string;
    /** Space names the group is linked to; non-empty means deletion is blocked. */
    linked_spaces?: string[];
}

/** Fetch all governance and entitlement groups from the backend DB in one call. */
export const listGroups = async (
    msalInstance: IMsalContext,
): Promise<IGroupListItem[]> => {
    const token = await getAccessToken(msalInstance);
    const bearerToken = "Bearer " + (token?.token ?? "");

    const requestURL = `${NEXUS_API_BASE_URL}/iam/groups?group_type=combined`;

    const response = await axios.get(requestURL, {
        headers: { Authorization: bearerToken },
    });

    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
};

/** Delete a governance group by its id. */
export const deleteGovernanceGroup = async (
    msalInstance: IMsalContext,
    id: string,
): Promise<void> => {
    const token = await getAccessToken(msalInstance);
    const bearerToken = "Bearer " + (token?.token ?? "");

    const requestURL = `${NEXUS_API_BASE_URL}/iam/governancegroups`;

    await axios.delete(requestURL, {
        headers: {
            Authorization: bearerToken,
            "Content-Type": "application/json",
        },
        data: { id },
    });
};

/** Delete an entitlement by its value (distinguished name). */
export const deleteEntitlement = async (
    msalInstance: IMsalContext,
    value: string,
): Promise<void> => {
    const token = await getAccessToken(msalInstance);
    const bearerToken = "Bearer " + (token?.token ?? "");

    const requestURL = `${NEXUS_API_BASE_URL}/iam/entitlements`;

    await axios.delete(requestURL, {
        headers: {
            Authorization: bearerToken,
            "Content-Type": "application/json",
        },
        data: { value },
    });
};

/** Deletion phase reported while a group pair is being removed. */
export type CombinedDeletePhase = "entitlement" | "governance";

/**
 * Delete a group pair in order: the entitlement first, then the governance
 * group. `onPhaseComplete` fires after each successful step so callers can
 * surface progress to the user.
 */
export const deleteCombinedGroup = async (
    msalInstance: IMsalContext,
    targets: { entitlementValue: string; governanceGroupId: string },
    onPhaseComplete?: (phase: CombinedDeletePhase) => void,
): Promise<void> => {
    await deleteEntitlement(msalInstance, targets.entitlementValue);
    onPhaseComplete?.("entitlement");

    await deleteGovernanceGroup(msalInstance, targets.governanceGroupId);
    onPhaseComplete?.("governance");
};
